import { useCallback, useEffect, useRef, useState } from 'react'
import type { Editor } from 'codemirror'
import { Controlled as CodeMirrorControlled, UnControlled as CodeMirrorUnControlled } from 'react-codemirror2'
import useResizeObserverFromRef from 'features/common/ui/useResizeObserverFromRef'
import { CodeAreaTheme } from './CodeAreaTheme'
import { findCodeAreaControlCodeOccurrences } from './utils'

// Core
import 'codemirror/lib/codemirror.css'
import 'codemirror/lib/codemirror.js'

// Modes
// Full available list here: https://codemirror.net/5/mode/index.html
import 'codemirror/mode/sql/sql.js'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/htmlmixed/htmlmixed.js'
import 'codemirror/mode/markdown/markdown.js'
import 'codemirror/mode/shell/shell.js'
import 'codemirror/mode/python/python.js'

// Addons
import 'codemirror/addon/display/placeholder'
import 'codemirror/addon/hint/sql-hint.js'
import 'codemirror/addon/hint/show-hint.css'
import 'codemirror/addon/hint/show-hint.js'
import 'codemirror/addon/selection/active-line.js'
import 'codemirror/addon/edit/matchbrackets.js'

// Folding
import 'codemirror/addon/fold/foldcode.js'
import 'codemirror/addon/fold/foldgutter.js'
import 'codemirror/addon/fold/foldgutter.css'
import 'codemirror/addon/fold/brace-fold.js'
import 'codemirror/addon/fold/xml-fold.js'
import 'codemirror/addon/fold/indent-fold.js'
import 'codemirror/addon/fold/markdown-fold.js'
import 'codemirror/addon/fold/comment-fold.js'

interface CodeAreaInputProps {
  value?: string
  defaultValue?: string
  options?: object
  id?: string
  onEditorDidMount?: (editor: Editor) => void
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  onFocus?: () => void
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
  height?: number | string
}

export default function CodeAreaInput({
  options,
  value,
  defaultValue,
  id,
  onEditorDidMount,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  disabled,
  size = 'small',
  height,
  ...props
}: CodeAreaInputProps) {
  // Refresh CodeMirror after any re-render that changes panel layout.
  const editorRef = useRef<Editor>()
  const boxRef = useResizeObserverFromRef(() => editorRef.current?.refresh())

  const codeMirrorOptions = {
    mode: 'sql',
    theme: 'light',
    lineNumbers: true,
    lineWrapping: true,
    extraKeys: { 'Ctrl-Space': 'autocomplete' },
    placeholder,
    disabled,
    gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
    styleActiveLine: { nonEmpty: true },
    styleActiveSelected: true,
    matchBrackets: true,
    foldOptions: {
      minFoldSize: 1,
      scanUp: false
    },
    ...options
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [editor, setEditor] = useState<any | null>(null)

  useEffect(() => {
    return () => {
      setEditor(null)
    }
  }, [])

  const foldCode = useCallback(
    ({ from, to }: { from: number; to?: number }) => {
      editor.foldCode(
        0,
        {
          rangeFinder: () => {
            if (!to) return
            return {
              from: { line: from - 1 },
              to: { line: to - 1 }
            }
          }
        },
        'fold'
      )
    },
    [editor]
  )

  useEffect(() => {
    if (!value || !editor) return
    const numberOfBlocks = findCodeAreaControlCodeOccurrences(value)
    if (!numberOfBlocks.length) return
    for (const line of numberOfBlocks) {
      foldCode(line)
    }
  }, [value, foldCode, editor])

  return (
    <CodeAreaTheme size={size} height={height} ref={boxRef} id={id} aria-label="code editor text area">
      {value !== undefined && (
        <CodeMirrorControlled
          {...props}
          value={value}
          editorDidMount={(editor) => {
            editorRef.current = editor
            onEditorDidMount?.(editor)
          }}
          options={codeMirrorOptions}
          onBeforeChange={(editor, data, value) => {
            if (onChange) {
              onChange(value)
            }
          }}
          onBlur={(editor) => {
            if (onBlur) {
              onBlur(editor.getValue())
            }
          }}
          onFocus={() => {
            if (onFocus) {
              onFocus()
            }
          }}
        />
      )}
      {defaultValue !== undefined && (
        <CodeMirrorUnControlled
          {...props}
          value={defaultValue}
          options={codeMirrorOptions}
          onBlur={(editor) => {
            if (onBlur) {
              onBlur(editor.getValue())
            }
          }}
          onFocus={() => {
            if (onFocus) {
              onFocus()
            }
          }}
        />
      )}
    </CodeAreaTheme>
  )
}
